// Campos obrigatórios por categoria, cadastrados no back office do iNOVAONE
// (Categorias > Campos Obrigatórios do Item → categoria.item_obrigatorio).
//
// As chaves são as mesmas do conta_config.obrigatorio ("info_compl_3",
// "nivel_2", "valor", "marca"...). A impressão só consegue exigir o que a tela
// deixa preencher: informações complementares, níveis de localização e valor.
// As demais (marca, foto, parceiro...) são do cadastro do item no back office
// e aqui são ignoradas — barrar a impressão por um campo que não existe na tela
// travaria o operador sem saída.
//
// Somam às obrigatórias da configuração de impressão
// (impressao_config.info_compl_obrigatorios): a categoria só acrescenta.

import { TOTAL_INFO_COMPL_MAXIMO } from "./infoCompl";

export type CategoriaComObrigatorios =
  | { item_obrigatorio?: string[] | string | null }
  | null
  | undefined;

/** Chaves obrigatórias da categoria. Tolera ausência e JSON string. */
export function chavesObrigatoriasDaCategoria(
  categoria: CategoriaComObrigatorios,
): string[] {
  let bruto: unknown = categoria?.item_obrigatorio;
  if (typeof bruto === "string") {
    try {
      bruto = JSON.parse(bruto);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(bruto)) return [];
  return bruto.filter((c): c is string => typeof c === "string");
}

/** Índices (1..12) das informações complementares que a categoria exige. */
export function infoComplObrigatoriasDaCategoria(
  categoria: CategoriaComObrigatorios,
): number[] {
  return chavesObrigatoriasDaCategoria(categoria)
    .map((chave) => /^info_compl_(\d+)$/.exec(chave))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => Number(m[1]))
    .filter((n) => n >= 1 && n <= TOTAL_INFO_COMPL_MAXIMO);
}

/**
 * Níveis de localização (2..4) que a categoria exige. O nível 1 já é sempre
 * obrigatório na impressão, então não entra aqui.
 */
export function niveisObrigatoriosDaCategoria(
  categoria: CategoriaComObrigatorios,
): number[] {
  return chavesObrigatoriasDaCategoria(categoria)
    .map((chave) => /^nivel_([2-4])$/.exec(chave))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => Number(m[1]));
}

/**
 * Níveis obrigatórios ainda vazios. Um nível sem nenhuma opção para escolher
 * (o nível de cima não tem filhos) não é cobrado — mesma regra do iNOVAONE
 * para campo que depende de cadastro.
 *
 * @param selecionados ids escolhidos nos níveis 1..4 (índice 0 = nível 1)
 * @param opcoesPorNivel quantas opções cada nível 1..4 oferece agora
 */
export function niveisObrigatoriosFaltando(
  categoria: CategoriaComObrigatorios,
  selecionados: string[],
  opcoesPorNivel: number[],
): number[] {
  return niveisObrigatoriosDaCategoria(categoria).filter(
    (nivel) => !selecionados[nivel - 1] && (opcoesPorNivel[nivel - 1] ?? 0) > 0,
  );
}

/** A categoria exige valor na informação individual de cada etiqueta. */
export function categoriaExigeValor(
  categoria: CategoriaComObrigatorios,
): boolean {
  return chavesObrigatoriasDaCategoria(categoria).includes("valor");
}
